import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { forkJoin, of } from 'rxjs';
import { Store } from '@ngrx/store';
import { AppState } from '../../../../core/reducers';
import { DocumentService } from '../services';
import {
  CancelCategoryPage,
  ClearCategoryError,
  CreateCategory,
  CreateCategoryOnServer,
  LoadCategoryPage,
  CategoryActionTypes,
  RequestCategoryPage,
  ThrowCategoryError,
  ToggleCategoryActionLoading,
  ToggleCategoryPageLoading,
  UpdateCategory,
  UpdateCategoryOnServer,
} from '../actions';

@Injectable()
export class CategoryEffects {
  showPageLoading = new ToggleCategoryPageLoading({ pageLoading: true });
  hidePageLoading = new ToggleCategoryPageLoading({ pageLoading: false });
  showActionLoading = new ToggleCategoryActionLoading({ actionLoading: true });
  hideActionLoading = new ToggleCategoryActionLoading({ actionLoading: false });

  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private documentService: DocumentService,
  ) {}

  @Effect()
  loadCategoryPage$ = this.actions$.pipe(
    ofType<RequestCategoryPage>(CategoryActionTypes.RequestCategoryPage),
    map((action: RequestCategoryPage) => action.payload),
    mergeMap(({ query }) => {
      this.store.dispatch(this.showPageLoading);
      const queryResult$ = this.documentService.getCategoryPage(query);
      const observables = [queryResult$, of(query)];

      return forkJoin(observables).pipe(
        catchError((err) => of(err)),
        map((res) => {
          if (res.length) {
            this.store.dispatch(new ClearCategoryError());
            this.store.dispatch(this.hidePageLoading);

            return new LoadCategoryPage({
              categories: res[0].data,
              totalCount: res[0].count,
              query: res[1],
            });
          }

          this.store.dispatch(new CancelCategoryPage());

          return new ThrowCategoryError({ error: res });
        }),
      );
    }),
  );

  @Effect()
  createCategory$ = this.actions$.pipe(
    ofType<CreateCategoryOnServer>(CategoryActionTypes.CreateCategoryOnServer),
    map((action: CreateCategoryOnServer) => action.payload),
    mergeMap(({ category }) => {
      this.store.dispatch(this.showActionLoading);

      return this.documentService.createCategory(category).pipe(
        catchError((err) => of(err)),
        map((res) => {
          if (res.status === 201) {
            this.store.dispatch(new ClearCategoryError());
            this.store.dispatch(this.hideActionLoading);

            return new CreateCategory({ category: res.body?.data });
          }

          return new ThrowCategoryError({ error: res });
        }),
      );
    }),
  );

  @Effect()
  updateCategory$ = this.actions$.pipe(
    ofType<UpdateCategoryOnServer>(CategoryActionTypes.UpdateCategoryOnServer),
    map((action: UpdateCategoryOnServer) => action.payload),
    mergeMap(({ category }) => {
      this.store.dispatch(this.showActionLoading);

      return this.documentService.updateCategory(category).pipe(
        catchError((err) => of(err)),
        map((res) => {
          if (res.status === 200) {
            this.store.dispatch(new ClearCategoryError());
            this.store.dispatch(this.hideActionLoading);

            return new UpdateCategory({ category: res.body?.data });
          }

          return new ThrowCategoryError({ error: res });
        }),
      );
    }),
  );
}
